import { APIClient, ApiQueryParams } from '@frontend/api-utils';
import {
    CreateTransactionRule,
    CreateTransactionRuleEntity,
    TransactionRule,
    TransactionRuleEntity,
    TransactionRuleEntityListResponse,
    TransactionRuleEntityQueryParams,
    TransactionRuleListResponse,
    TransactionRuleQueryParams,
    UpdateTransactionRule
} from '@frontend/transaction/types';

export class TransactionRuleClient extends APIClient {
    public static readonly endpoint = '/transaction-api/v1';

    public static async fetchTransactionRules(queryParams?: ApiQueryParams<TransactionRuleQueryParams>): Promise<TransactionRuleListResponse> {
        return await this.apiPaginated<TransactionRuleListResponse, TransactionRuleQueryParams>(`${this.endpoint}/transaction-rules`, queryParams);
    }

    public static async resolveTransactionRules(ids: string[], field: string = 'id'): Promise<TransactionRuleListResponse> {
        return this.resolve<TransactionRuleListResponse>(`${this.endpoint}/transaction-rules`, ids, field);
    }

    public static async deleteTransactionRules(ids: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/transaction-rules-delete`, { ids });
        return await this.handleVoidResponse(response);
    }

    public static async postTransactionRule(accountId: string, body: CreateTransactionRule): Promise<TransactionRule> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/transaction-rules`, body);
        return await this.handleResponse<TransactionRule>(response);
    }

    public static async fetchTransactionRule(accountId: string, ruleId: string): Promise<TransactionRule> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/transaction-rules/${ruleId}`);
        return await this.handleResponse<TransactionRule>(response);
    }

    public static async patchTransactionRule(accountId: string, ruleId: string, body: UpdateTransactionRule): Promise<TransactionRule> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/transaction-rules/${ruleId}`, body);
        return await this.handleResponse<TransactionRule>(response);
    }

    public static async deleteTransactionRule(accountId: string, ruleId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/transaction-rules/${ruleId}`);
        return await this.handleVoidResponse(response);
    }

    public static async fetchTransactionRuleEntities(
        queryParams?: ApiQueryParams<TransactionRuleEntityQueryParams>
    ): Promise<TransactionRuleEntityListResponse> {
        return await this.apiPaginated<TransactionRuleEntityListResponse, TransactionRuleEntityQueryParams>(
            `${this.endpoint}/transaction-rule-entities`,
            queryParams
        );
    }

    public static async deleteTransactionRuleEntities(ids: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/transaction-rule-entities-delete`, { ids });
        return await this.handleVoidResponse(response);
    }

    public static async resolveTransactionRuleEntities(ids: string[], field: string = 'id'): Promise<TransactionRuleEntityListResponse> {
        return this.resolve<TransactionRuleEntityListResponse>(`${this.endpoint}/transaction-rule-entities`, ids, field);
    }

    public static async postTransactionRuleEntity(accountId: string, ruleId: string, body: CreateTransactionRuleEntity): Promise<TransactionRuleEntity> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/transaction-rules/${ruleId}/entities`, body);
        return await this.handleResponse<TransactionRuleEntity>(response);
    }

    public static async fetchTransactionRuleEntity(accountId: string, ruleId: string, ruleEntityId: string): Promise<TransactionRuleEntity> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/transaction-rules/${ruleId}/entities/${ruleEntityId}`);
        return await this.handleResponse<TransactionRuleEntity>(response);
    }

    public static async deleteTransactionRuleEntity(accountId: string, ruleId: string, ruleEntityId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/transaction-rules/${ruleId}/entities/${ruleEntityId}`);
        return await this.handleVoidResponse(response);
    }
}
